import { useEffect, useMemo, useState } from "react";
import { Box, Button, Paper, Stack, Typography, useTheme } from "@mui/material";
import moment from "moment";
import {
  convertToUTC,
  convertUtcToLocalDateTime,
  formatPayoutDateTime,
} from "../../../utils/Util";
import { startOfDay, subDays } from "date-fns";
import { DateRangePickerCustom } from "../../../common/components/daterangepicker.custom";
import { useGetStoreDepositsQuery } from "../../../services/API/reportApiSlice";
import {
  Filter,
  IStoreDepositsRequest,
  IStoreDepositsResponse,
} from "../../../types/types";
import { GetDealerPlatformFeeOption } from "../../../utils/UtilTarget";
import { DealerLocationFilter } from "../../DashboardApp/dashboard.dealerlocationfilter";
import { DashboardDataLoader } from "../../DashboardApp/dashboard.dataloader";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { Download as DownloadIcon } from "@mui/icons-material";
import DataGridProComponent from "./DataGridProComponent";
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  getGridDateOperators,
  getGridNumericOperators,
  getGridStringOperators,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { useAppSelector } from "../../../app/hooks";
import { RootState } from "../../../app/store";

// Format Number
const formatNumber = (
  value: number | string,
  isPinnedRow: boolean = false,
  isPercentageColumn: boolean = false,
): string => {
  const num = Number(value);
  if (isNaN(num)) return String(value);

  const absFormatted = Math.abs(num).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const prefix = isPinnedRow && !isPercentageColumn ? "$" : "";

  return num < 0 ? `(${prefix}${absFormatted})` : `${prefix}${absFormatted}`;
};

export default function DepositsReport() {
  const theme = useTheme();
  const today = new Date();

  const defaultDateRange: [Date, Date] = [
    subDays(startOfDay(today), 1),
    subDays(startOfDay(today), 1),
  ];

  const dealerId = useAppSelector((state: RootState) => state.dealer.dealerId);
  const [selectedDateRange, setSelectedDateRange] =
    useState<[Date, Date]>(defaultDateRange);
  const [storeDeposits, setStoreDeposits] = useState<IStoreDepositsResponse[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [sortField, setSortField] = useState("payoutDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [IsPlatformFeeEnabled, setIsPlatformFeeEnabled] =
    useState<boolean>(false);
  const [hasSurchargeEverEnabled, SetHasSurchargeEverEnabled] =
    useState<boolean>(false);
  const [dealerLocations, setDealerLocations] = useState<any>([]);
  const [dealerLocationId, setDealerLocationId] = useState<number>(-1);
  const [logicOperator, setLogicOperator] = useState<"and" | "or">("and");
  const apiRef = useGridApiRef();
  const payoutTimezone =
    storeDeposits.find((d) => d.timezone)?.timezone || "EST";

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      surchargeAmountPercentage: false,
      platformFeePercentage: false,
      transactions: false,
    });

  const [pricingModel, setPricingModel] = useState<string>("");
  const [hasfeeOrSurcharge, setHasFeeOrSurcharge] = useState<boolean>(false);
  // Extract valid filters with values
  const activeFilterItems = useMemo(() => {
    return filterModel.items.filter(
      (item) =>
        item.value !== undefined && item.value !== null && item.value !== "",
    );
  }, [filterModel]);

  const dateColumns = ["transactionDate", "payoutDate"];
  const percentageColumns = [
    "platformFeePercentage",
    "surchargeAmountPercentage",
  ];

  const depositsRequest: IStoreDepositsRequest = useMemo(() => {
    const filters: Filter[] = activeFilterItems.map((item) => {
      const itemValue = item.value?.toString() ?? "";

      const isDateColumn = dateColumns.includes(item.columnField);
      const startValue = isDateColumn ? convertToUTC(itemValue) : itemValue;
      const endValue = isDateColumn ? convertToUTC(itemValue, "end") : "";

      return {
        field: item.columnField,
        operator: item.operatorValue,
        startValue: startValue,
        endValue: endValue,
      };
    });

    return {
      store: localStorage.getItem("storeReference"),
      payoutDateFrom: convertToUTC(selectedDateRange[0]),
      payoutDateTo: convertToUTC(selectedDateRange[1], "end"),
      dealerId: dealerLocationId,
      sortField,
      sortOrder,
      logicOperator,
      filters,
    };
  }, [
    selectedDateRange,
    dealerLocationId,
    sortField,
    sortOrder,
    activeFilterItems,
  ]);

  //RTK Query hook to get store deposits.
  const { data, error, isError, isFetching, requestId } =
    useGetStoreDepositsQuery(depositsRequest, {
      refetchOnMountOrArgChange: true,
    });

  const [lastRequestId, setLastRequestId] = useState<string | undefined>();

  useEffect(() => {
    // Only set loading when a new request is in progress and is different from the last
    if (isFetching && requestId !== lastRequestId) {
      setLastRequestId(requestId);
      setIsLoading(true);
    }

    if (!isFetching && requestId === lastRequestId) {
      setIsLoading(false);
    }
  }, [isFetching, requestId]);

  useEffect(() => {
    document.title = `Deposit | ${theme.browserTitle}`;
    setIsPlatformFeeEnabled(GetDealerPlatformFeeOption());
    SetHasSurchargeEverEnabled(
      localStorage.getItem("hasSurchargeEverEnabled") === "true",
    );

    const GetDealerLocations = async () => {
      try {
        const response = await DashboardDataLoader.GetDealerLocations(dealerId);
        setDealerLocations(response);
      } catch (error) {
        console.error("Error fetching Dealer Locations:", error);
      }
    };

    GetDealerLocations();
  }, []);

  useEffect(() => {
    if (apiRef.current) {
      // Reset scroll to top-left
      apiRef.current.scrollToIndexes({ rowIndex: 0, colIndex: 0 });
    }

    if (data || error) {
      setStoreDeposits(data ?? []);
      setPricingModel(data.length > 0 ? data[0].pricingModel : "");

      const hasFeeOrSurchargeinTransactions =
        Array.isArray(data) &&
        data.some(
          (item) => item.platformFee !== 0 || item.surchargeAmount !== 0,
        );

      setHasFeeOrSurcharge(hasFeeOrSurchargeinTransactions);
      setIsLoading(false);
    }
  }, [data, error]);

  // Assigning Id to each row
  const allRows = useMemo(() => {
    return storeDeposits.map((item) => {
      return {
        id: item.id, // DataGridPro needs a unique 'id'
        ...item,
      };
    });
  }, [storeDeposits]);

  // Getting all rows except Total Row
  const rows = useMemo(() => {
    return allRows.filter((row) => row.batchNumber !== "Total");
  }, [allRows]);

  // Getting Total Row
  const totalRow = useMemo(() => {
    return allRows.find((row) => row.batchNumber === "Total");
  }, [allRows]);

  // Calculate GL sum for visible rows
  const glSum = useMemo(() => {
    return allRows
      .filter((row) => {
        const hasFeeOrSurcharge =
          row.platformFee !== 0 || row.surchargeAmount !== 0;
        return (
          row.pricingModel === "Flat Rate" &&
          hasFeeOrSurcharge &&
          row.batchNumber !== "Total"
        );
      })
      .reduce((sum, row) => sum + (Number(row.glAmount) || 0), 0);
  }, [allRows]);

  const handleDateRangePickerChange = (value: [Date, Date]) => {
    setSelectedDateRange(value);
  };

  const handleDealerLocationChange = (value: number) => {
    setDealerLocationId(value);
  };

  const handleFilterModelChange = (model: GridFilterModel) => {
    setFilterModel(model);
    setLogicOperator(model.linkOperator);
  };

  // to handle the indexes of columns dynamically
  let surchargeAmountIndex = -1;
  let surchargePercentageIndex = -1;
  let platformFeeIndex = -1;
  let platformFeePercentageIndex = -1;

  // Column Headers
  const headers = [
    `Payout (${payoutTimezone})`,
    "Batch",
    "Transaction",
    "Type",
    "Processing Method",
    "PSP Reference",
    "Merchant Reference",
    "Sales ($)",
    "Refunds ($)",
  ];

  if (hasSurchargeEverEnabled) {
    surchargeAmountIndex = headers.length;
    headers.push("Surcharge ($)");

    surchargePercentageIndex = headers.length;
    headers.push("Surcharge %");
  }
  if (IsPlatformFeeEnabled) {
    platformFeeIndex = headers.length;
    headers.push("Platform Fee ($)");

    platformFeePercentageIndex = headers.length;
    headers.push("Platform Fee %");
  }

  const transactionsIndex = headers.length;

  headers.push(
    "Transactions ($)",
    "Refunds Total ($)",
    "Chargeback ($)",
    "Total ($)",
    "Processing Fees ($)",
    "Payout ($)",
    "-/+ GL ($)",
  );

  const handleExportToExcel = () => {
    try {
      const rows = storeDeposits
        .filter((store) => store.batchNumber !== "Total")
        .map((store) => {
          const row = [
            formatPayoutDateTime(store.payoutDateInTimezone),
            store.batchNumber,
            store.transactionDate
              ? convertUtcToLocalDateTime(store.transactionDate)
              : "",
            store.transactionType,
            store.processingMethod,
            store.pspReference,
            store.merchantReference,
            formatNumber(store.saleAmount),
            formatNumber(store.refunds),
          ];

          if (hasSurchargeEverEnabled) {
            row.push(
              formatNumber(store.surchargeAmount),
              formatNumber(store.surchargeAmountPercentage),
            );
          }

          if (IsPlatformFeeEnabled) {
            row.push(
              formatNumber(store.platformFee),
              formatNumber(store.platformFeePercentage),
            );
          }

          row.push(
            formatNumber(store.transactions),
            formatNumber(store.refundsTotal),
            formatNumber(store.chargeback),
            formatNumber(store.totalTransactionsMinusRefunds),
            formatNumber(store.fee),
            formatNumber(store.totalPayoutAmount),
          );

          if (
            pricingModel === "Flat Rate" &&
            (store.platformFee !== 0 || store.surchargeAmount !== 0)
          ) {
            row.push(formatNumber(store.glAmount));
          } else {
            // Push empty value to maintain column alignment
            row.push("-");
          }

          return row;
        });

      // Add Empty row
      rows.push([]);

      // Add total row if exists
      if (totalRow) {
        const total = [
          totalRow.batchNumber,
          "",
          "",
          "",
          "",
          "",
          "",
          formatNumber(totalRow.saleAmount),
          formatNumber(totalRow.refunds),
        ];
        if (hasSurchargeEverEnabled) {
          total.push(
            formatNumber(totalRow.surchargeAmount),
            "", // No surcharge percentage total
          );
        }
        if (IsPlatformFeeEnabled) {
          total.push(
            formatNumber(totalRow.platformFee),
            "", // no platform percentage total
          );
        }

        total.push(
          formatNumber(totalRow.transactions),
          formatNumber(totalRow.refundsTotal),
          formatNumber(totalRow.chargeback),
          formatNumber(totalRow.totalTransactionsMinusRefunds),
          formatNumber(totalRow.fee),
          formatNumber(totalRow.totalPayoutAmount),
        );

        // Show sum of GL column for visible rows in Total row
        if (pricingModel === "Flat Rate" && hasfeeOrSurcharge) {
          total.push(formatNumber(glSum));
        } else {
          total.push("-");
        }

        rows.push(total);
      }

      const worksheetData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Deposits");

      const store = localStorage.getItem("storeReference");
      const payoutDateFrom = moment
        .utc(selectedDateRange[0])
        .local()
        .format("DD-MM-YYYY");
      const payoutDateTo = moment
        .utc(selectedDateRange[1])
        .local()
        .format("DD-MM-YYYY");

      const fileName = `deposit_report_${store}_${payoutDateFrom}_${payoutDateTo}.xlsx`;

      XLSX.writeFile(workbook, fileName);

      toast.success("File downloaded successfully.");
    } catch (error) {
      console.error("Failed to export Excel file:", error);
      toast.error("Export failed. Please try again.");
    }
  };

  const renderAmountCell = (params: any) => {
    const isPinnedRow = params.row?.batchNumber === "Total";
    const value = Number(params.value);
    const isPercentageColumn = percentageColumns.includes(params.field);
    const formatted = formatNumber(value, isPinnedRow, isPercentageColumn);
    const color = value > 0 ? "green" : value < 0 ? "red" : "inherit";
    const size = isPinnedRow ? "15px" : "inherit";

    return <span style={{ color, fontSize: size }}>{formatted}</span>;
  };

  const numericOperators = getGridNumericOperators().filter(
    (operator) =>
      operator.value !== "isAnyOf" &&
      operator.value !== "isEmpty" &&
      operator.value !== "isNotEmpty",
  );

  const stringOperators = getGridStringOperators().filter(
    (operator) =>
      operator.value !== "isAnyOf" &&
      operator.value !== "isEmpty" &&
      operator.value !== "isNotEmpty",
  );

  const dateOperators = getGridDateOperators().filter(
    (operator) =>
      operator.value !== "isEmpty" &&
      operator.value !== "isNotEmpty" &&
      operator.value !== "not",
  );

  const columns: GridColDef[] = [
    {
      field: "payoutDateInTimezone",
      minWidth: 190,
      headerName: headers[0],
      type: "date",
      filterOperators: dateOperators,
      valueFormatter: (params) => formatPayoutDateTime(params.value),
    },
    {
      field: "batchNumber",
      minWidth: 120,
      headerName: headers[1],
      type: "number",
      filterOperators: numericOperators,
      renderCell: ({ row, value }) =>
        row?.batchNumber === "Total" ? "" : <span>{value}</span>,
    },
    {
      field: "transactionDate",
      minWidth: 190,
      headerName: headers[2],
      type: "date",
      filterOperators: dateOperators,
      valueFormatter: (params) => convertUtcToLocalDateTime(params.value),
    },
    {
      field: "transactionType",
      minWidth: 120,
      headerName: headers[3],
      type: "string",
      filterOperators: stringOperators,
      renderCell: ({ value }) => {
        const colorMap = {
          capture: "green",
          refund: "red",
        } as const;

        return (
          <span style={{ color: colorMap[value] ?? "inherit" }}>{value}</span>
        );
      },
    },
    {
      field: "processingMethod",
      minWidth: 220,
      headerName: headers[4],
      type: "string",
      filterOperators: stringOperators,
      renderCell: ({ row, value }) =>
        row?.batchNumber === "Total" ? "" : <span>{value}</span>,
    },
    {
      field: "pspReference",
      minWidth: 200,
      headerName: headers[5],
      type: "string",
      filterOperators: stringOperators,
    },
    {
      field: "merchantReference",
      minWidth: 220,
      headerName: headers[6],
      type: "string",
      filterOperators: stringOperators,
      renderCell: ({ row, value }) => {
        const isTotalRow = row?.batchNumber === "Total";
        return (
          <span
            style={{
              fontSize: isTotalRow ? "20px" : "inherit",
              display: "flex",
              alignItems: "center",
            }}
          >
            {isTotalRow ? (
              <>
                <SummarizeIcon fontSize="medium" style={{ marginRight: 4 }} />
                Total
              </>
            ) : (
              value
            )}
          </span>
        );
      },
    },
    {
      field: "saleAmount",
      minWidth: 160,
      headerName: headers[7],
      type: "number",
      filterOperators: numericOperators,
      renderCell: (params) => renderAmountCell(params),
    },
    {
      field: "refunds",
      minWidth: 160,
      headerName: headers[8],
      type: "number",
      filterOperators: numericOperators,
      renderCell: (params) => renderAmountCell(params),
    },
  ];
  if (hasSurchargeEverEnabled) {
    columns.push(
      {
        field: "surchargeAmount",
        minWidth: 180,
        headerName: headers[surchargeAmountIndex],
        type: "number",
        filterOperators: numericOperators,
        renderCell: (params) => renderAmountCell(params),
      },
      {
        field: "surchargeAmountPercentage",
        minWidth: 180,
        headerName: headers[surchargePercentageIndex],
        type: "number",
        filterOperators: numericOperators,
        renderCell: (params) => renderAmountCell(params),
      },
    );
  }

  if (IsPlatformFeeEnabled) {
    columns.push(
      {
        field: "platformFee",
        minWidth: 220,
        headerName: headers[platformFeeIndex],
        type: "number",
        filterOperators: numericOperators,
        renderCell: (params) => renderAmountCell(params),
      },
      {
        field: "platformFeePercentage",
        minWidth: 180,
        headerName: headers[platformFeePercentageIndex],
        type: "number",
        filterOperators: numericOperators,
        renderCell: (params) => renderAmountCell(params),
      },
    );
  }
  columns.push(
    {
      field: "transactions",
      minWidth: 220,
      headerName: headers[transactionsIndex],
      type: "number",
      filterOperators: numericOperators,
      renderCell: (params) => renderAmountCell(params),
    },
    {
      field: "refundsTotal",
      minWidth: 220,
      headerName: headers[transactionsIndex + 1],
      type: "number",
      filterOperators: numericOperators,
      renderCell: (params) => renderAmountCell(params),
    },
    {
      field: "chargeback",
      minWidth: 180,
      headerName: headers[transactionsIndex + 2],
      type: "number",
      filterOperators: numericOperators,
      renderCell: (params) => renderAmountCell(params),
    },
    {
      field: "totalTransactionsMinusRefunds",
      minWidth: 180,
      headerName: headers[transactionsIndex + 3],
      type: "number",
      filterOperators: numericOperators,
      renderCell: (params) => renderAmountCell(params),
    },
    {
      field: "fee",
      minWidth: 220,
      headerName: headers[transactionsIndex + 4],
      type: "number",
      filterOperators: numericOperators,
      renderCell: (params) => renderAmountCell(params),
    },
    {
      field: "totalPayoutAmount",
      minWidth: 160,
      headerName: headers[transactionsIndex + 5],
      type: "number",
      filterOperators: numericOperators,
      renderCell: (params) => renderAmountCell(params),
    },
    {
      field: "glAmount",
      minWidth: 140,
      headerName: headers[transactionsIndex + 6],
      type: "number",
      filterOperators: numericOperators,
      renderCell: (params) => {
        const { row, value } = params;
        const isTotal = row.batchNumber === "Total";
        const hasfeeOrSurcharge =
          row.platformFee !== 0 || row.surchargeAmount !== 0;
        const isGLColumnVisible =
          row?.pricingModel === "Flat Rate" && hasfeeOrSurcharge;

        // For Total row, show sum if ANY row matches the condition
        if (isTotal) {
          const anyRowMatches = allRows.some(
            (r) =>
              r.pricingModel === "Flat Rate" &&
              (r.platformFee !== 0 || r.surchargeAmount !== 0),
          );
          return (
            <span>
              {anyRowMatches ? renderAmountCell({ row, value: glSum }) : "-"}
            </span>
          );
        }
        return (
          <span>
            {isGLColumnVisible ? renderAmountCell({ row, value }) : "-"}
          </span>
        );
      },
    },
  );

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h6" fontWeight="bold">
          Deposits
        </Typography>
        <Stack direction="row" alignItems="center">
          <DealerLocationFilter
            dealerLocationsID={dealerLocationId}
            dealerLocations={dealerLocations}
            onDealerLocationChange={handleDealerLocationChange}
          ></DealerLocationFilter>
          <DateRangePickerCustom
            dateRange={defaultDateRange}
            onDateRangePickerChange={handleDateRangePickerChange}
            isDepositReport={true}
          ></DateRangePickerCustom>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{ ml: 2 }}
            onClick={handleExportToExcel}
            disabled={!storeDeposits.length}
          >
            Export
          </Button>
        </Stack>
      </Stack>
      <Paper sx={{ position: "relative" }}>
        <DataGridProComponent
          rows={rows}
          columns={columns}
          pinnedTotalRow={totalRow}
          isLoading={isLoading}
          onFilterModelChange={handleFilterModelChange}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          apiRefProp={apiRef}
          showCustomToolbarColumnsButton={true}
          customToolbarColumnsText={true}
          customSx={{
            "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
              visibility: "visible !important",
              opacity: 1,
              width: "auto !important",
              pointerEvents: "auto",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
              display: "flex !important",
              visibility: "visible !important",
            },
            // Prevent header collapsing because of the extra icon
            "& .MuiDataGrid-columnHeaderTitleContainer": {
              maxWidth: "calc(100% - 25px)", // leave space for icon
            },
          }}
        />
      </Paper>
    </Box>
  );
}
